<template>
  <div class="experiment-files">
    <div class="files-header">
      <div class="title-wrap">
        <h3>Files</h3>
        <span class="meta-count">{{ mergedEntities.length }} item(s)</span>
      </div>

      <div class="header-right hide-on-collapse">
        <div v-if="isUploading" class="upload-progress-inline">
          <span class="percent">{{ uploadPercent }}%</span>
          <progress :value="uploadPercent" max="100"></progress>
          <span class="label" v-if="uploadLabel">{{ uploadLabel }}</span>
        </div>

        <div class="bulk-actions">
          <button
            class="bulk-btn"
            :disabled="selectedFileIds.length === 0"
            :class="{ 'disabled-btn': selectedFileIds.length === 0 }"
            @click="downloadSelectedFiles"
            title="Download selected"
          >
            <i class="bi bi-download"></i>
          </button>
          <button
            class="bulk-btn"
            :disabled="selectedFileIds.length === 0"
            :class="{ 'disabled-btn': selectedFileIds.length === 0 }"
            @click="deleteSelectedFiles"
            title="Delete selected"
          >
            <i class="bi bi-trash"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="files-container">
      <!-- Folder Section (left column) -->
      <div class="folder-section" :class="{ 'is-collapsed': isFolderTreeCollapsed }">
        <div class="folder-header">
          <span v-if="!isFolderTreeCollapsed" class="folder-section-title">Folders</span>
          <div v-else class="folder-header-spacer"></div>
          <div class="folder-header-actions">
            <button
              type="button"
              class="folder-collapse-btn"
              :title="isFolderTreeCollapsed ? 'Show folder tree' : 'Hide folder tree'"
              :aria-label="isFolderTreeCollapsed ? 'Show folder tree' : 'Hide folder tree'"
              @click="toggleFolderTree"
            >
              <i class="bi" :class="isFolderTreeCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'"></i>
            </button>
          </div>
        </div>
        <template v-if="!isFolderTreeCollapsed">
          <div class="home-folder-wrapper">
            <div
              class="folder-item home-folder"
              :class="{ active: !currentFolder }"
              @click="goHome"
              title="Home (Root)"
            >
              <i class="bi bi-house-door-fill"></i> Home
            </div>
          </div>
          <!-- Folder List -->
          <div class="folder-list">
            <!-- FolderTree is rendered as before -->
            <FolderTree
              v-for="folder in folderTree"
              :key="folder.id"
              :folder="folder"
              :activeFolderId="currentFolder ? currentFolder.id : null"
              @enterFolder="enterFolder"
            />
          </div>
        </template>
      </div>

      <!-- Files Table Section (right column) -->
      <div class="files-table-section" :class="{ loading: isLoadingTreeEffective }">
        <div class="current-path-bar">
          <button type="button" class="path-btn" @click="goHome" :disabled="!currentFolder" title="Go to root">
            <i class="bi bi-house-door-fill"></i>
          </button>
          <button
            type="button"
            class="path-btn"
            @click="goBackFolder"
            :disabled="!currentFolder"
            title="Go up one level"
          >
            <i class="bi bi-arrow-up"></i>
          </button>
          <div v-if="!isReadOnlyContext" class="add-folder-wrapper">
            <button
              type="button"
              class="path-btn add-folder-btn"
              @click="toggleFolderInput"
              title="Create Folder"
            >
              <i class="bi bi-folder-plus"></i>
            </button>
            <transition name="fade">
              <div v-if="showFolderInput" class="folder-input-dropdown" @click.stop>
                <input
                  type="text"
                  v-model="newFolderName"
                  placeholder="Folder Name"
                  autofocus
                />
                <div class="folder-input-actions">
                  <button @click="cancelFolder" class="cancel-btn">✖</button>
                  <button @click="saveFolder" class="save-btn">✔</button>
                </div>
              </div>
            </transition>
          </div>
          <span class="current-path" :title="currentPathDisplay">{{ currentPathDisplay }}</span>
        </div>

        <div class="table-scroll" ref="fileTableScroll">
          <table class="file-table">
            <thead>
              <tr>
                <th class="type-header">
                  <input
                    type="checkbox"
                    :checked="allVisibleFilesSelected"
                    :disabled="visibleFileIds.length === 0"
                    :title="allVisibleFilesSelected ? 'Unselect all files' : 'Select all files'"
                    @click.stop="toggleSelectAllVisibleFiles"
                  />
                </th>
                <th>Name</th>
                <th class="actions-header">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in paginatedEntities"
                :key="item.id + '-' + item.entityType"
                class="file-row"
                :class="{
                  'file-row--selected': item.entityType === 'file' && selectedFileIds.includes(item.id),
                  'file-row--selectable': item.entityType === 'file'
                }"
                @mouseover="hoveredEntityId = item.id"
                @mouseleave="hoveredEntityId = null"
                @mousedown.left="startFileDragSelection(item, $event)"
                @mouseenter="handleFileDragEnter(item)"
                @mouseup="endFileDragSelection"
                @click="handleRowClick(item)"
              >
              <td class="type-header">
                <i
                  v-if="item.entityType === 'folder'"
                  class="bi"
                  :class="isEntityFolder(item) ? 'bi-diagram-3-fill' : 'bi-folder-fill'"
                ></i>
                <i v-else class="bi bi-file-earmark"></i>
              </td>
              <td class="file-name-cell" v-tooltip="item.entityType === 'folder' ? item.name : getFileName(item.path)">
                <!-- If this is a folder, allow inline editing -->
                <template v-if="item.entityType === 'folder'">
                  <template v-if="editingFolderId !== item.id">
                    <span class="file-name clickable" @click.stop="item.entityType === 'folder' && enterFolder(item)">
                      {{ item.name }}
                      <span v-if="isEntityFolder(item)" class="entity-folder-tag">
                        {{ entityTypeLabel(item.display_entity_label || item.entity_type) }}
                      </span>
                    </span>
                  </template>
                  <template v-else-if="!isGroupFolder(item)">
                    <input
                      type="text"
                      v-model="editingFolderName"
                      class="rename-input"
                      @keyup.enter="confirmRenameFolder(item)"
                      @blur="confirmRenameFolder(item)"
                    />
                  </template>
                </template>
                <!-- For files just show the name -->
                <template v-else>
                  <span class="file-name">
                    {{ getFileName(item.path) }}
                  </span>
                </template>
              </td>
              <td class="actions-cell">
                <div
                  class="row-actions"
                  :class="{
                    'row-actions--visible': hoveredEntityId === item.id || activeComment === item.id || selectedFileIds.includes(item.id)
                  }"
                >
                  <div v-if="item.entityType === 'file' && canToggleDisplayImage(item)" class="display-image-toggle">
                    <button
                      type="button"
                      class="display-image-toggle__btn"
                      :class="{ 'display-image-toggle__btn--active': isDisplayImage(item) }"
                      :disabled="displayImageSavingIds.includes(item.id)"
                      :title="isDisplayImage(item) ? 'Remove from display images' : 'Use as display image'"
                      @click.stop="toggleDisplayImage(item)"
                    >
                      <i :class="displayImageSavingIds.includes(item.id) ? 'bi bi-arrow-repeat' : (isDisplayImage(item) ? 'bi bi-star-fill' : 'bi bi-star')"></i>
                    </button>
                  </div>
                  <div class="row-actions__secondary">
                    <div v-if="item.entityType === 'file' || (item.entityType === 'folder' && !isGroupFolder(item))" class="comment-container">
                      <button
                        type="button"
                        class="row-action-btn comment-action-btn"
                        :class="{ 'row-action-btn--active': activeComment === item.id, 'row-action-btn--has-value': Boolean(item.comment?.text) }"
                        :title="item.comment?.text ? 'View or edit comment' : 'Add comment'"
                        @click.stop="toggleComment(item, $event)"
                        @mouseover="hoveredComment = item.id; updateTooltipPos($event)"
                        @mouseleave="hoveredComment = null"
                      >
                        <i :class="item.comment?.text ? 'bi bi-chat-left-text-fill' : 'bi bi-chat-left-text'"></i>
                      </button>
                    </div>
                    <button
                      v-if="item.entityType === 'folder' && isEntityFolder(item)"
                      type="button"
                      class="row-action-btn"
                      :title="`Go to ${entityTypeLabel(item.display_entity_label || item.entity_type)}`"
                      @click.stop="navigateToEntity(item)"
                    >
                      <i class="bi bi-arrow-right-short"></i>
                    </button>
                    <template v-if="item.entityType === 'folder' && !isEntityFolder(item) && !isGroupFolder(item)">
                      <button
                        type="button"
                        class="row-action-btn"
                        title="Rename folder"
                        @click.stop="startEditingFolder(item)"
                      >
                        <i class="bi bi-pencil"></i>
                      </button>
                      <button
                        v-if="deletingFolderId !== item.id"
                        type="button"
                        class="row-action-btn"
                        title="Delete folder"
                        @click.stop="confirmDeleteFolder(item)"
                      >
                        <i class="bi bi-trash"></i>
                      </button>
                      <span
                        v-else
                        class="row-action-btn row-action-btn--static"
                        title="Deleting folder"
                      >
                        <i class="bi bi-arrow-repeat folder-delete-spinner"></i>
                      </span>
                    </template>
                    <button
                      v-if="item.entityType === 'file'"
                      type="button"
                      class="row-action-btn"
                      title="Edit file"
                      @click.stop="openCollaboraFile(item)"
                    >
                      <i class="bi bi-pencil-square"></i>
                    </button>
                  </div>
                  <div v-if="hoveredComment === item.id && item.comment?.text" class="comment-tooltip" :style="{ top: tooltipPos.top + 'px', left: tooltipPos.left + 'px' }" @click.stop>
                    {{ item.comment.text }}
                  </div>
                </div>
              </td>
              </tr>
              <tr v-if="paginatedEntities.length === 0 && !isLoadingTreeEffective">
                <td colspan="3" class="empty-state">No files or folders in this location.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="isLoadingTreeEffective" class="table-loading-overlay">
          <div class="loading-content">
            <span class="tree-loading-spinner"></span>
            <span>Loading files and folders...</span>
          </div>
        </div>

        <!-- Pagination Controls -->
        <div class="pagination" v-if="totalPages > 1">
          <button
            v-for="page in visiblePageNumbers"
            :key="page"
            @click="setPage(page)"
            :class="{ active: page === currentPage }"
          >
            {{ page }}
          </button>
        </div>
      </div>
    </div>

    <div class="upload-controls">
      <div class="upload-info">
        <span class="selected-count">{{ selectedFilesCount }} file(s) selected</span>

        <!-- Upload files button -->
        <i
          v-if="!isReadOnlyContext"
          class="bi bi-file-earmark-arrow-up-fill upload-icon"
          @click="$refs.fileInputFiles.click()"
          title="Upload Files"
        ></i>

        <!-- Upload folder button -->
        <i
          v-if="!isReadOnlyContext"
          class="bi bi-folder-plus upload-icon"
          @click="$refs.fileInputFolder.click()"
          title="Upload Folder"
        ></i>

        <button
          v-if="selectedFiles.length > 0 && !isReadOnlyContext"
          type="button"
          @click="addFiles"
          class="upload-btn"
        >
          Upload
        </button>

        <i
          class="bi bi-x-square-fill cancel-icon"
          v-if="selectedFilesCount > 0"
          @click="cancelSelection"
        ></i>
      </div>
    </div>

<input
  ref="fileInputFiles"
  type="file"
  class="hidden"
  multiple
  @change="handleFileUpload"
/>

<input
  ref="fileInputFolder"
  type="file"
  class="hidden"
  webkitdirectory
  @change="handleFileUpload"
/>

    <CollaboraEditorModal
      :visible="collaboraModalOpen"
      :editorUrl="collaboraEditorUrl"
      :title="collaboraFileLabel"
      @close="closeCollaboraEditor"
    />

    <Teleport to="body">
      <div
        v-if="activeCommentItem"
        class="comment-editor-backdrop"
        @click.self="cancelEdit"
      >
        <div
          class="comment-editor comment-editor--popover"
          :style="{ top: `${commentEditorPos.top}px`, left: `${commentEditorPos.left}px` }"
          @click.stop
        >
          <div class="comment-editor__header">
            <strong>{{ activeCommentItem.comment?.text ? 'Edit comment' : 'Add comment' }}</strong>
            <button type="button" class="comment-editor__close" @click="cancelEdit" aria-label="Close comment editor">×</button>
          </div>
          <textarea v-model="editedComment" placeholder="Enter comment..."></textarea>
          <div class="comment-actions">
            <button @click="cancelEdit" class="cancel-btn">✖</button>
            <button @click="saveComment(activeCommentItem)" class="save-btn">✔</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { API_ENDPOINTS } from "@/services/config";
import { useToast } from "vue-toastification";
import apiService from "@/services/apiService";
import axios from "axios";
import FolderTree from "@/components/FolderTree.vue";
import CollaboraEditorModal from "@/components/CollaboraEditorModal.vue";
import { confirmDelete } from "@/utils/confirmDelete";
import { extractNumericId, flattenTreeFolders, groupVirtualChildren } from "@/utils/filesTree";
import { mapState } from 'vuex';
import {
  DISPLAY_IMAGE_UPDATED_EVENT,
  emitDisplayImagesUpdated,
  getDisplayImageOrder,
  isDisplayImageEligible,
  matchesDisplayImageEntity,
} from '@/utils/displayImages';

export default {
  props: {
    files: {
      type: Array,
      required: true,
    },
    loadingTree: {
      type: Boolean,
      default: false,
    },
    folders: {
      type: Array,
      default: () => [],
    },
    basePath: {
      type: String,
      default: '',
    },
    experimentId: {
      type: Number,
      required: true,
    },
    assType: {
      type: String,
      required: true,
    },
    assId: {
      type: Number,
      required: true,
    },
    isUploading: { 
      type: Boolean, 
      default: false 
    },
    uploadPercent: { 
      type: Number, 
      default: 0 
    },
    uploadLabel: { 
      type: String, 
      default: "" 
    },
  },
  emits: ["addFiles", "removeFile"],
  components: {
    FolderTree,
    CollaboraEditorModal,
  },
  data() {
    return {
      selectedFiles: [],
      selectedFileIds: [],
      isFileDragSelecting: false,
      fileDragSelectAction: null,
      suppressNextFileClick: false,
      hoveredEntityId: null,
      activeComment: null,
      activeCommentItem: null,
      activeCommentAnchor: null,
      editedComment: "",
      hoveredComment: null,
      commentEditorPos: { top: 0, left: 0 },
      tooltipPos: { top: 0, left: 0 },
      showFolderInput: false,
      newFolderName: "",
      foldersFetched: [],
      isTreeLoading: false,
      currentFolder: null,
      // Pagination data
      currentPage: 1,
      filesPerPage: 10,
      // Data properties for inline folder renaming:
      editingFolderId: null,
      editingFolderName: "",
      deletingFolderId: null,
      locallyHiddenFileIds: [],
      collaboraModalOpen: false,
      collaboraEditorUrl: "",
      collaboraFileLabel: "Collabora Editor",
      displayImages: [],
      displayImageSavingIds: [],
      isFolderTreeCollapsed: true,
    };
  },
  computed: {
    ...mapState(['loggedInUser']),
    associativeId() {
      return this.assId ?? this.experimentId ?? null;
    },
    displayImageLookup() {
      return (this.displayImages || []).reduce((lookup, image, index) => {
        if (image?.file_id != null) {
          lookup[image.file_id] = {
            image,
            order: getDisplayImageOrder(image, index),
          };
        }
        return lookup;
      }, {});
    },
    // Merge folders from prop and fetched state so local refreshes are visible immediately
    rawFoldersList() {
      const propFolders = Array.isArray(this.folders) ? this.folders : [];
      const fetchedFolders = Array.isArray(this.foldersFetched) ? this.foldersFetched : [];
      const merged = [...propFolders, ...fetchedFolders];

      const byId = new Map();
      merged.forEach((folder) => {
        if (folder) {
          const dedupeKey = folder.internal_path || folder.path || String(folder.id);
          if (dedupeKey) {
            byId.set(dedupeKey, folder);
          }
        }
      });

      return Array.from(byId.values());
    },
    foldersList() {
      return groupVirtualChildren(this.rawFoldersList);
    },
    selectedFilesCount() {
      return this.selectedFiles.length;
    },
    visibleFileIds() {
      return (this.filteredFiles || []).map((file) => file.id).filter((id) => id != null);
    },
    allVisibleFilesSelected() {
      return this.visibleFileIds.length > 0
        && this.visibleFileIds.every((id) => this.selectedFileIds.includes(id));
    },
    currentPathDisplay() {
      if (!this.currentFolder) {
        return "Home";
      }
      const parts = [];
      let node = this.currentFolder;
      while (node) {
        parts.unshift(node.name || "Unnamed");
        node = this.foldersList.find((f) => f.id === node.parent_id) || null;
      }
      return parts.length ? `Home / ${parts.join(" / ")}` : "Home";
    },
    isLoadingTreeEffective() {
      return Boolean(this.loadingTree || this.isTreeLoading);
    },
    isReadOnlyContext() {
      if (!this.currentFolder) return false;
      return this.isGroupFolder(this.currentFolder);
    },
    rootFolderPath() {
      if (this.basePath) {
        return this.normalizePath(this.basePath);
      }
      if (this.rawFoldersList.length > 0) {
        const rootFolders = this.rawFoldersList.filter((folder) => !folder?.parent_id);
        const parentPaths = Array.from(
          new Set(
            rootFolders
              .map((folder) => this.normalizePath(folder?.parent_path || ""))
              .filter(Boolean)
          )
        );

        if (parentPaths.length === 1) {
          return parentPaths[0];
        }

        const samplePath = this.normalizePath(this.rawFoldersList[0]?.internal_path || this.rawFoldersList[0]?.path || "");
        return this.getParentPath(samplePath);
      }
      return "";
    },
    folderTree() {
      if (!this.foldersList) return [];
      const validFolders = this.foldersList.filter(folder => folder != null);
      const map = {};
      validFolders.forEach(folder => {
        folder.children = [];
        map[folder.id] = folder;
      });
      const roots = [];
      validFolders.forEach(folder => {
        if (folder.parent_id) {
          if (map[folder.parent_id]) {
            map[folder.parent_id].children.push(folder);
          }
        } else {
          roots.push(folder);
        }
      });
      return roots;
    },
    filteredFiles() {
      const visibleFiles = this.files.filter(
        (file) => !this.locallyHiddenFileIds.includes(file.id)
      );

      if (!this.currentFolder) {
        const basePath = this.normalizePath(this.rootFolderPath);
        if (!basePath) return visibleFiles;

        return visibleFiles.filter((file) => {
          const fileParent = this.getParentPath(file?.path || "");
          return fileParent === basePath;
        });
      } else {
        // Group folders only hold child entities; their parent's files belong to the parent.
        if (String(this.currentFolder.type || "").toLowerCase() === "virtual_group") return [];

        const currentPath = this.normalizePath(this.currentFolder?.internal_path || this.currentFolder?.path || "");
        const currentEntity = this.getEntityContextFromFolder(this.currentFolder);
        if (!currentPath && !currentEntity) return visibleFiles;

        const currentFolderId = this.currentFolder?.id != null ? String(this.currentFolder.id) : "";
        const currentBackendId = this.currentFolder?.backend_id != null ? String(this.currentFolder.backend_id) : "";

        return visibleFiles.filter((file) => {
          const fileFolderId = file?.folder_id != null ? String(file.folder_id) : "";
          const directFolderMatch = Boolean(
            fileFolderId && (
              fileFolderId === currentFolderId
              || (currentBackendId && fileFolderId === currentBackendId)
              || (currentBackendId && fileFolderId === `folder:${currentBackendId}`)
            )
          );

          if (directFolderMatch) {
            return true;
          }

          const filePath = this.normalizePath(file?.path || "");
          const fileParent = this.getParentPath(filePath);
          const pathMatches = fileParent === currentPath;

          if (pathMatches) {
            return true;
          }

          if (!currentEntity) {
            return false;
          }

          if (fileFolderId) {
            return false;
          }

          const fileEntityType = String(file?.entity_type || file?.file_type || "").toLowerCase();
          const fileEntityId = Number(file?.entity_id ?? file?.associative_id ?? file?.folder_entity_id);
          return Boolean(
            fileEntityType
            && fileEntityId
            && fileEntityType === String(currentEntity.associativeType || "").toLowerCase()
            && fileEntityId === Number(currentEntity.associativeId)
          );
        });
      }
    },
    mergedEntities() {
      const validFolders = this.foldersList.filter(folder => folder);
      const fileItems = this.filteredFiles.map(file => ({
        ...file,
        entityType: "file",
      }));

      fileItems.sort((a, b) =>
        this.getFileName(a.path).localeCompare(this.getFileName(b.path), undefined, {
          sensitivity: "base",
          numeric: true,
        })
      );

      if (!this.currentFolder && this.assType === "project") {
        const rootFolders = this.folderTree
          .map(folder => ({ ...folder, entityType: "folder" }));

        const customRootFolders = rootFolders
          .filter((folder) => String(folder?.type || '').toLowerCase() === 'folder')
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
              numeric: true,
            })
          );

        const structureRootFolders = rootFolders
          .filter((folder) => String(folder?.type || '').toLowerCase() === 'structure_element' || String(folder?.entity_type || '').toLowerCase() === 'project_structure_element')
          .sort((a, b) =>
            String(a.name || "").localeCompare(String(b.name || ""), undefined, {
              sensitivity: "base",
              numeric: true,
            })
          );

        const experimentGroups = rootFolders.filter(
          (folder) => this.getRootEntityGroupType(folder) === "experiment"
        );
        const otherEntityGroups = rootFolders.filter((folder) => {
          const type = this.getRootEntityGroupType(folder);
          return type && type !== "experiment";
        });

        otherEntityGroups.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base",
            numeric: true,
          })
        );

        return [
          ...structureRootFolders,
          ...otherEntityGroups,
          ...experimentGroups,
          ...customRootFolders,
          ...fileItems,
        ];
      }

      let folderItems = [];
      if (this.currentFolder) {
        folderItems = validFolders
          .filter(folder => folder.parent_id === this.currentFolder.id)
          .map(folder => ({ ...folder, entityType: "folder" }));
      } else {
        folderItems = this.folderTree.map(folder => ({
          ...folder,
          entityType: "folder",
        }));
      }

      folderItems.sort((a, b) => this.compareFolderItems(a, b));

      return [...folderItems, ...fileItems];
    },
    paginatedEntities() {
      const startIndex = (this.currentPage - 1) * this.filesPerPage;
      return this.mergedEntities.slice(startIndex, startIndex + this.filesPerPage);
    },
    totalPages() {
      return Math.ceil(this.mergedEntities.length / this.filesPerPage);
    },
    visiblePageNumbers() {
      const range = 10;
      let startPage = Math.max(1, this.currentPage - Math.floor(range / 2));
      let endPage = startPage + range - 1;
      if (endPage > this.totalPages) {
        endPage = this.totalPages;
        startPage = Math.max(1, endPage - range + 1);
      }
      return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  watch: {
    associativeId: {
      immediate: true,
      handler(newVal) {
        // Only fetch if folders not already provided via props
        if (newVal && (!this.folders || this.folders.length === 0)) {
          this.fetchFolders();
        }
        this.fetchDisplayImages();
      },
    },
    files: {
      immediate: true,
      handler() {
        this.displayImages = (this.displayImages || []).filter((image) =>
          this.files.some((file) => Number(file?.id) === Number(image?.file_id))
        );
      },
    },
  },
  mounted() {
    window.addEventListener(DISPLAY_IMAGE_UPDATED_EVENT, this.handleDisplayImagesUpdated);
    window.addEventListener('resize', this.repositionActiveCommentEditor);
    window.addEventListener('scroll', this.repositionActiveCommentEditor, true);
  },
  beforeUnmount() {
    window.removeEventListener(DISPLAY_IMAGE_UPDATED_EVENT, this.handleDisplayImagesUpdated);
    window.removeEventListener('resize', this.repositionActiveCommentEditor);
    window.removeEventListener('scroll', this.repositionActiveCommentEditor, true);
  },
  methods: {
    handleDisplayImagesUpdated(event) {
      if (matchesDisplayImageEntity(event?.detail, this.assType, this.associativeId)) {
        this.fetchDisplayImages();
      }
    },
    toggleFileSelection(item) {
      if (item?.entityType !== 'file' || item?.id == null) return;

      if (this.selectedFileIds.includes(item.id)) {
        this.selectedFileIds = this.selectedFileIds.filter((id) => id !== item.id);
      } else {
        this.selectedFileIds = [...this.selectedFileIds, item.id];
      }
    },
    toggleSelectAllVisibleFiles() {
      const fileIds = this.visibleFileIds;
      if (!fileIds.length) return;

      if (this.allVisibleFilesSelected) {
        this.selectedFileIds = this.selectedFileIds.filter((id) => !fileIds.includes(id));
      } else {
        this.selectedFileIds = Array.from(new Set([...this.selectedFileIds, ...fileIds]));
      }
    },
    startFileDragSelection(item, event) {
      if (!item || item.entityType !== 'file' || item.id == null) return;
      if (event?.button != null && event.button !== 0) return;

      this.isFileDragSelecting = true;
      this.fileDragSelectAction = this.selectedFileIds.includes(item.id) ? 'unselect' : 'select';
      this.applyFileDragSelection(item);
    },
    handleFileDragEnter(item) {
      if (!this.isFileDragSelecting) return;
      this.applyFileDragSelection(item);
    },
    applyFileDragSelection(item) {
      if (!item || item.entityType !== 'file' || item.id == null) return;

      const alreadySelected = this.selectedFileIds.includes(item.id);
      if (this.fileDragSelectAction === 'select' && !alreadySelected) {
        this.selectedFileIds = [...this.selectedFileIds, item.id];
      } else if (this.fileDragSelectAction === 'unselect' && alreadySelected) {
        this.selectedFileIds = this.selectedFileIds.filter((id) => id !== item.id);
      }
    },
    endFileDragSelection() {
      if (this.isFileDragSelecting) {
        this.suppressNextFileClick = true;
      }
      this.isFileDragSelecting = false;
      this.fileDragSelectAction = null;
    },
    async fetchDisplayImages() {
      if (!this.assType || !this.associativeId || !this.loggedInUser?.entra_id) {
        this.displayImages = [];
        return;
      }

      try {
        const response = await apiService.getEntityDisplayImages(
          this.assType,
          this.associativeId,
          this.loggedInUser.entra_id
        );
        const images = Array.isArray(response?.display_images)
          ? response.display_images
          : Array.isArray(response)
            ? response
            : [];
        this.displayImages = images;
      } catch (error) {
        if (error?.response?.status === 404) {
          this.displayImages = [];
          return;
        }
        console.error('Error fetching display images:', error);
      }
    },
    belongsToCurrentEntity(file) {
      if (!file) return false;

      const normalizedAssType = String(this.assType || '').toLowerCase();
      const fileAssociationType = String(file.associative_type || file.file_type || '').toLowerCase();
      const fileAssociationId = Number(file.associative_id ?? file.entity_id ?? this.associativeId);
      const inheritedEntity = this.getEntityContextFromFolder(this.currentFolder);

      if (fileAssociationType === 'comment') return false;
      if (!fileAssociationType) {
        if (!inheritedEntity) return true;
        return String(inheritedEntity.associativeType || '').toLowerCase() === normalizedAssType
          && Number(inheritedEntity.associativeId) === Number(this.associativeId);
      }
      if (fileAssociationType !== normalizedAssType) return false;
      return Number(this.associativeId) === fileAssociationId;
    },
    canToggleDisplayImage(file) {
      return this.belongsToCurrentEntity(file) && isDisplayImageEligible(file);
    },
    isDisplayImage(file) {
      return Boolean(this.displayImageLookup?.[file?.id]);
    },
    displayImageOrder(file) {
      return this.displayImageLookup?.[file?.id]?.order ?? null;
    },
    async toggleDisplayImage(file) {
      if (!file?.id || !this.canToggleDisplayImage(file)) return;
      if (this.displayImageSavingIds.includes(file.id)) return;

      this.displayImageSavingIds = [...this.displayImageSavingIds, file.id];
      try {
        if (this.isDisplayImage(file)) {
          await apiService.unmarkDisplayImage(file.id, this.loggedInUser.entra_id);
          this.toast.success('Display image removed.');
        } else {
          await apiService.markDisplayImage(file.id, this.loggedInUser.entra_id);
          this.toast.success('Display image added.');
        }

        await this.fetchDisplayImages();
        emitDisplayImagesUpdated(this.assType, this.associativeId);
      } catch (error) {
        console.error('Error toggling display image:', error);
        this.toast.error('Failed to update display image.');
      } finally {
        this.displayImageSavingIds = this.displayImageSavingIds.filter((id) => id !== file.id);
      }
    },
    async fetchFolders() {
      try {
        if (!this.associativeId) return;
        this.isTreeLoading = true;
        try {
          // Try new endpoint first
          const response = await axios.get(API_ENDPOINTS.filesTree, {
            params: {
              file_type: this.assType,
              entity_id: this.associativeId,
              entra_id: this.loggedInUser.entra_id
            },
          });
          const treeRoot = response.data.tree || null;
          const nestedFolders = Array.isArray(treeRoot?.children) ? treeRoot.children : [];
          this.foldersFetched = flattenTreeFolders(nestedFolders);
          console.log('New endpoint: Folders fetched:', this.foldersFetched.length);
        } catch (error) {
          // Fallback to old endpoint
          console.warn('New endpoint failed, using legacy:', error.message);
          const response = await axios.get(API_ENDPOINTS.folders, {
            params: {
              associative_type: this.assType,
              associative_id: this.associativeId,
              entra_id: this.loggedInUser.entra_id
            },
          });
          this.foldersFetched = response.data || [];
          console.log('Legacy endpoint: Folders fetched:', this.foldersFetched.length);
        }
        // Fetch comments for each folder
        this.foldersFetched.forEach((folder) => {
          const commentEntityId = folder?.backend_id ?? (typeof folder?.id === "number" ? folder.id : null);
          if (!folder.comment && commentEntityId != null) {
            this.fetchComment(commentEntityId, "folder", folder);
          }
        });
      } catch (error) {
        console.error("Error fetching folders:", error);
      } finally {
        this.isTreeLoading = false;
      }
    },
    isEntityFolder(folder) {
      if (!folder) return false;
      const folderType = String(folder?.type || '').toLowerCase();
      if (folderType === 'folder' && !folder?.virtual) return false;
      if (folderType === 'virtual_group') return false;
      if (!folder?.entity_type || folder.entity_id == null) return false;
      return String(folder.entity_type).toLowerCase() !== 'project';
    },
    isGroupFolder(folder) {
      if (String(folder?.type || '').toLowerCase() === 'virtual_group') return true;
      const path = String(folder?.path || "");
      return Boolean(
        folder?.virtual
        && folder?.entity_type == null
        && (path.includes("/__structure_group__/") || path.includes("/__experiments__/"))
      );
    },
    navigateToEntity(folder) {
      const routeMap = {
        workflow:   { name: 'WorkflowDetailPage',   params: { id: folder.entity_id } },
        action:     { name: 'ActionDetailPage',      params: { id: folder.entity_id } },
        experiment: { name: 'ExperimentDetailPage',  params: { id: folder.entity_id } },
        structure_element: {
          name: 'StructureElementDetailPage',
          params: {
            projectId: Number(this.$route?.params?.projectId || this.$route?.params?.id || 0),
            elementId: folder.entity_id,
          },
        },
        project:    { name: 'ProjectDetailPage',     params: { id: folder.entity_id } },
        material:   { name: 'MaterialDetailPage',    params: { id: folder.entity_id } },
        record:     { name: 'RecordDetailPage',      params: { id: folder.entity_id } },
        location:   { name: 'LocationDetailPage',    params: { id: folder.entity_id } },
        instrument: { name: 'InstrumentDetailPage',  params: { id: folder.entity_id } },
      };
      const route = routeMap[String(folder.entity_type).toLowerCase()];
      if (route) this.$router.push(route);
    },
    isRootEntityGroupFolder(folder) {
      const path = this.normalizePath(folder?.path || "");
      return Boolean(
        folder?.type === 'virtual_group'
        && (
          path.includes("/__structure_group__/")
          || path.endsWith("/__experiments__")
          || path.includes("/__experiments__/" )
          || path.includes("/__entity_group__/" )
        )
      );
    },
    getRootEntityGroupType(folder) {
      const path = this.normalizePath(folder?.path || "");
      if (!path) return "";

      if (folder?.type === 'virtual_group') {
        if (path.includes('/experiments')) {
          return 'experiment';
        }
      }

      const structureMarker = "/__structure_group__/";
      const experimentsMarker = "/__experiments__";
      const legacyMarker = "/__entity_group__/";

      if (path.includes(structureMarker)) {
        const idx = path.indexOf(structureMarker);
        return path.slice(idx + structureMarker.length).split("/")[0] || "";
      }

      if (path.endsWith(experimentsMarker) || path.includes(`${experimentsMarker}/`)) {
        return "experiment";
      }

      if (path.includes(legacyMarker)) {
        const idx = path.indexOf(legacyMarker);
        return path.slice(idx + legacyMarker.length).split("/")[0] || "";
      }

      return "";
    },
    entityTypeLabel(type) {
      const normalized = String(type || '').trim();
      const map = {
        action: 'Activity',
        workflow: 'Workflow',
        experiment: 'Experiment',
        project_structure_element: 'Structure Element',
        structure_element: 'Structure Element',
        virtual_group: 'Group',
      };
      return map[normalized] || (normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '');
    },
    compareFolderItems(left, right) {
      // Synthetic group folders first, then real folders.
      const leftIsGroup = String(left?.type || "").toLowerCase() === "virtual_group";
      const rightIsGroup = String(right?.type || "").toLowerCase() === "virtual_group";
      if (leftIsGroup !== rightIsGroup) return leftIsGroup ? -1 : 1;

      const leftType = String(left?.entity_type || "").toLowerCase();
      const rightType = String(right?.entity_type || "").toLowerCase();

      if (leftType === "action" && rightType === "action") {
        const leftOrder = Number(left?.order);
        const rightOrder = Number(right?.order);
        const leftHasOrder = Number.isFinite(leftOrder);
        const rightHasOrder = Number.isFinite(rightOrder);

        if (leftHasOrder && rightHasOrder && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }

        if (leftHasOrder !== rightHasOrder) {
          return leftHasOrder ? -1 : 1;
        }
      }

      return String(left?.name || "").localeCompare(String(right?.name || ""), undefined, {
        sensitivity: "base",
        numeric: true,
      });
    },
    getEntityContextFromFolder(folder) {
      let current = folder || null;
      while (current) {
        if (current.entity_type && current.entity_id != null) {
          const normalizedType = String(current.entity_type).toLowerCase() === 'project_structure_element'
            ? 'structure_element'
            : String(current.entity_type).toLowerCase();
          return {
            associativeType: normalizedType,
            associativeId: current.entity_id,
          };
        }
        current = this.foldersList.find((f) => f.id === current.parent_id) || null;
      }
      return null;
    },
    getTargetAssociation() {
      const inheritedEntity = this.getEntityContextFromFolder(this.currentFolder);
      const currentFolderId = this.currentFolder && !this.isEntityFolder(this.currentFolder) && !this.isGroupFolder(this.currentFolder)
        ? (this.currentFolder.backend_id ?? null)
        : null;
      if (inheritedEntity) {
        return {
          ...inheritedEntity,
          parentId: currentFolderId,
        };
      }
      return {
        associativeType: this.assType,
        associativeId: this.associativeId,
        parentId: currentFolderId,
      };
    },
    async fetchComment(id, type = "file", folderContext = null) {
      try {
        const inheritedEntity = folderContext ? this.getEntityContextFromFolder(folderContext) : this.getEntityContextFromFolder(this.currentFolder);
        const associativeType = type === 'folder' && inheritedEntity
          ? inheritedEntity.associativeType
          : this.assType;
        const associativeId = type === 'folder' && inheritedEntity
          ? inheritedEntity.associativeId
          : this.associativeId;

        const response = await axios.get(API_ENDPOINTS.associativeComments, {
          params: { 
            comment_type: type,
            entity_id: id,
            associative_type: associativeType,
            associative_id: associativeId,
            entra_id: this.loggedInUser.entra_id
           },
        });
        if (response.data.length > 0) {
          const comment = response.data[0];
          if (type === "file") {
            const file = this.files.find(f => f.id === id);
            if (file) {
              file.comment = comment;
            }
          } else if (type === "folder") {
            const folder = this.rawFoldersList.find(f => (f.backend_id ?? f.id) === id);
            if (folder) {
              folder.comment = comment;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching comment:", error);
      }
    },
    updateTooltipPos(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.tooltipPos = {
        top: Math.max(12, rect.bottom + 6),
        left: Math.max(12, Math.min(rect.left, window.innerWidth - 262)),
      };
    },
    positionCommentEditor(anchorElement) {
      if (!anchorElement) {
        return;
      }

      const rect = anchorElement.getBoundingClientRect();
      const panelWidth = Math.min(420, Math.max(window.innerWidth - 32, 220));
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const margin = 12;
      const estimatedHeight = 220;

      let left = rect.right - panelWidth;
      if (left < margin) {
        left = margin;
      }
      if (left + panelWidth > viewportWidth - margin) {
        left = Math.max(margin, viewportWidth - margin - panelWidth);
      }

      let top = rect.bottom + 8;
      if (top + estimatedHeight > viewportHeight - margin) {
        top = Math.max(margin, rect.top - estimatedHeight - 8);
      }

      this.commentEditorPos = { top, left };
    },
    repositionActiveCommentEditor() {
      if (!this.activeCommentAnchor || !this.activeCommentItem) {
        return;
      }

      this.positionCommentEditor(this.activeCommentAnchor);
    },
    toggleComment(item, event) {
      if (this.activeComment === item.id) {
        this.activeComment = null;
        this.activeCommentItem = null;
        this.activeCommentAnchor = null;
        this.editedComment = "";
      } else {
        this.activeComment = item.id;
        this.activeCommentItem = item;
        this.activeCommentAnchor = event?.currentTarget || null;
        this.editedComment = item.comment ? item.comment.text : "";
        this.$nextTick(() => {
          this.positionCommentEditor(this.activeCommentAnchor);
        });
      }
    },
    async saveComment(item) {
      try {
        if (item.comment && item.comment.id) {
          await apiService.updateResource(
            `${API_ENDPOINTS.comments}/${item.comment.id}`,
            { 
              text: this.editedComment 
            },
            this.loggedInUser.id,
            this.loggedInUser.entra_id
          );
          item.comment.text = this.editedComment;
        } else {
          const commentEntityId =
            item.entityType === "folder"
              ? (item.backend_id ?? null)
              : item.id;

          if (item.entityType === "folder" && commentEntityId == null) {
            this.toast.info("Comments are not available for virtual entity folders.");
            return;
          }

          const newComment = {
            text: this.editedComment,
            author_id: this.loggedInUser.id,
            entity_id: commentEntityId,
            comment_type: item.entityType,
          };

          const response = await apiService.createResource(API_ENDPOINTS.comments, newComment, this.loggedInUser.entra_id);
          item.comment = response.data;
          this.fetchComment(commentEntityId, item.entityType);
        }
        this.activeComment = null;
        this.activeCommentItem = null;
        this.activeCommentAnchor = null;
        this.editedComment = "";
        this.toast.success("Comment saved!");
      } catch (error) {
        console.error("Error saving comment:", error);
        this.toast.error("Failed to save comment.");
      }
    },
    cancelEdit() {
      this.activeComment = null;
      this.activeCommentItem = null;
      this.activeCommentAnchor = null;
      this.editedComment = "";
    },
    normalizeFileNameKey(fileName) {
      return String(fileName || "").trim().toLowerCase();
    },
    splitFileName(fileName) {
      const safeName = String(fileName || "").trim() || "file";
      const lastDot = safeName.lastIndexOf(".");
      if (lastDot <= 0) {
        return { base: safeName, extension: "" };
      }
      return {
        base: safeName.slice(0, lastDot),
        extension: safeName.slice(lastDot),
      };
    },
    buildUniqueFileName(fileName, reservedNames) {
      const { base, extension } = this.splitFileName(fileName);
      let candidate = `${base}${extension}`;
      let attempt = 1;

      while (reservedNames.has(this.normalizeFileNameKey(candidate))) {
        candidate = `${base} (${attempt})${extension}`;
        attempt += 1;
      }

      return candidate;
    },
    autoRenameConflictingSelectedFiles(files) {
      const selected = Array.isArray(files) ? files : [];
      if (selected.length === 0) {
        return [];
      }

      const existingNames = new Set(
        (this.filteredFiles || []).map((file) =>
          this.normalizeFileNameKey(this.getFileName(file.path))
        )
      );
      const reservedNames = new Set(existingNames);
      let renamedCount = 0;

      const updatedFiles = selected.map((file) => {
        const isFolderUploadEntry = Boolean(file?.webkitRelativePath);
        if (isFolderUploadEntry || !file?.name) {
          if (file?.name) {
            reservedNames.add(this.normalizeFileNameKey(file.name));
          }
          return file;
        }

        const uniqueName = this.buildUniqueFileName(file.name, reservedNames);
        let resultingFile = file;

        if (uniqueName !== file.name) {
          try {
            resultingFile = new File([file], uniqueName, {
              type: file.type,
              lastModified: file.lastModified,
            });
            renamedCount += 1;
          } catch (error) {
            console.warn("Unable to rename selected file before upload:", error);
          }
        }

        reservedNames.add(this.normalizeFileNameKey(resultingFile.name || file.name));
        return resultingFile;
      });

      if (renamedCount > 0) {
        this.toast.info(
          `${renamedCount} file name${renamedCount === 1 ? " was" : "s were"} adjusted to avoid duplicates.`
        );
      }

      return updatedFiles;
    },
    handleFileUpload(event) {
      const incomingFiles = Array.from(event.target.files || []);
      this.selectedFiles = this.autoRenameConflictingSelectedFiles(incomingFiles);
    },
    addFiles() {
      if (!this.associativeId) {
        this.toast.error("Missing associative id for file upload");
        return;
      }
      const target = this.getTargetAssociation();
      if (!target.associativeId || !target.associativeType) {
        this.toast.error("Missing target entity for file upload");
        return;
      }
      console.log('Upload target payload:', {
        fileType: target.associativeType,
        entityId: target.associativeId,
        folderId: target.parentId,
        associativeType: target.associativeType,
        associativeId: target.associativeId,
        currentFolderId: this.currentFolder ? (this.currentFolder.backend_id ?? this.currentFolder.id ?? null) : null,
      });
      this.$emit("addFiles", {
        files: this.selectedFiles,
        id: target.associativeId,
        fileType: target.associativeType,
        folderId: target.parentId,
      });
      this.fetchFolders(); 
      this.selectedFiles = [];
      if (this.$refs.fileInputFiles) {
        this.$refs.fileInputFiles.value = "";
      }
      if (this.$refs.fileInputFolder) {
        this.$refs.fileInputFolder.value = "";
      }
    },
    cancelSelection() {
      this.selectedFiles = [];
      if (this.$refs.fileInputFiles) {
        this.$refs.fileInputFiles.value = "";
      }
      if (this.$refs.fileInputFolder) {
        this.$refs.fileInputFolder.value = "";
      }
    },
    removeFile(fileId) {
      this.$emit("removeFile", fileId);
    },
    getFileName(filePath) {
      if (!filePath) return "Unknown File";
      return filePath.replace(/\\/g, "/").split("/").pop();
    },
    normalizePath(path) {
      if (!path) return "";
      return String(path)
        .replace(/\\/g, "/")
        .replace(/\/+$/, "");
    },
    getParentPath(path) {
      const normalized = this.normalizePath(path);
      if (!normalized) return "";
      const lastSlash = normalized.lastIndexOf("/");
      return lastSlash === -1 ? "" : normalized.substring(0, lastSlash);
    },
    getDownloadLink(file) {
      const entraId = this.loggedInUser.entra_id;
      return `${API_ENDPOINTS.file(file.id)}?entra_id=${encodeURIComponent(entraId)}`;
    },
    async openCollaboraFile(file) {
      if (!file?.id) return;
      if (!this.loggedInUser?.entra_id) {
        this.toast.error("You must be logged in before opening a document.");
        return;
      }

      try {
        const response = await apiService.createWopiSession(file.id, this.loggedInUser.entra_id, true);
        const editorUrl = response?.editor_url || response?.editorUrl || response?.editor || "";
        const wopiSrc = response?.wopi_src || response?.wopiSrc || response?.wopi_url || response?.wopiUrl || "";
        const accessToken = response?.access_token || response?.accessToken || "";

        let finalEditorUrl = editorUrl;
        if (!finalEditorUrl && wopiSrc) {
          finalEditorUrl = `http://localhost:9980/browser/dist/cool.html?WOPISrc=${encodeURIComponent(wopiSrc)}`;
          if (accessToken) {
            finalEditorUrl += `&access_token=${encodeURIComponent(accessToken)}`;
          }
        }

        if (!finalEditorUrl) {
          throw new Error("The server did not return an editor URL.");
        }

        this.collaboraFileLabel = this.getFileName(file.path) || `File ${file.id}`;
        this.collaboraEditorUrl = finalEditorUrl;
        this.collaboraModalOpen = true;
      } catch (error) {
        console.error("Failed to open Collabora session:", error);
        const message = error?.response?.data?.message || error?.message || "Unable to open the document in Collabora.";
        this.toast.error(message);
      }
    },
    closeCollaboraEditor() {
      this.collaboraModalOpen = false;
      this.collaboraEditorUrl = "";
      this.collaboraFileLabel = "Collabora Editor";
    },
    formatDate(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    },
    truncateText(text, maxLength = 30) {
      return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
    },
    handleRowClick(item) {
      if (this.suppressNextFileClick) {
        this.suppressNextFileClick = false;
        return;
      }

      if (item?.entityType !== "file" || item?.id == null) {
        return;
      }

      if (this.selectedFileIds.includes(item.id)) {
        this.selectedFileIds = this.selectedFileIds.filter((id) => id !== item.id);
      } else {
        this.selectedFileIds = [...this.selectedFileIds, item.id];
      }

      this.$nextTick(() => this.updateSelectAllCheckboxState());
    },
    downloadSelectedFiles() {
      if (this.selectedFileIds.length === 1) {
        const file = this.files.find(f => f.id === this.selectedFileIds[0]);
        if (file) {
          const link = document.createElement("a");
          link.href = this.getDownloadLink(file);
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else if (this.selectedFileIds.length > 1) {
        axios
          .post(
            API_ENDPOINTS.zipFiles,
            { fileIds: this.selectedFileIds,
              entra_id: this.loggedInUser.entra_id,
             },
            { responseType: "blob" }
          )
          .then(response => {
            const blob = new Blob([response.data], { type: "application/zip" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = "files.zip";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          })
          .catch(error => {
            console.error("Error downloading zip:", error);
            this.toast.error("Failed to download files as zip.");
          });
      }
    },
    async deleteSelectedFiles() {
      const isConfirmed = await confirmDelete("Are you sure you want to delete this File(s)?");
      if (!isConfirmed) return;
      this.selectedFileIds.forEach(fileId => {
        this.removeFile(fileId);
      });
      this.selectedFileIds = [];
    },
    toggleFolderInput() {
      this.showFolderInput = !this.showFolderInput;
    },
    toggleFolderTree() {
      this.isFolderTreeCollapsed = !this.isFolderTreeCollapsed;
      if (this.isFolderTreeCollapsed) {
        this.showFolderInput = false;
      }
    },
    async saveFolder() {
      if (!this.newFolderName.trim()) {
        this.toast.error("Folder name cannot be empty");
        return;
      }
      if (!this.associativeId) {
        this.toast.error("Missing associative id for folder creation");
        return;
      }
      try {
        const target = this.getTargetAssociation();
        if (!target.associativeId || !target.associativeType) {
          this.toast.error("Missing target entity for folder creation");
          return;
        }
        const payload = {
          name: this.newFolderName,
          associative_type: target.associativeType,
          associative_id: target.associativeId,
          parent_id: target.parentId,
        };
        await apiService.createResource(API_ENDPOINTS.folders, payload, this.loggedInUser.entra_id);
        await this.fetchFolders();
        this.toast.success("Folder created!");
        this.newFolderName = "";
        this.showFolderInput = false;
      } catch (error) {
        console.error("Error creating folder:", error);
        this.toast.error("Failed to create folder.");
      }
    },
    cancelFolder() {
      this.newFolderName = "";
      this.showFolderInput = false;
    },
    enterFolder(folder) {
      this.currentFolder = folder;
      // Reset pagination when navigating folders
      this.currentPage = 1;
      this.scrollTableToTop();
    },
    goBackFolder() {
      if (this.currentFolder && this.currentFolder.parent_id) {
        const parent = this.foldersList.find(f => f.id === this.currentFolder.parent_id);
        this.currentFolder = parent || null;
      } else {
        this.currentFolder = null;
      }
      this.currentPage = 1;
      this.scrollTableToTop();
    },
    goHome() {
      this.currentFolder = null;
      this.currentPage = 1;
      this.scrollTableToTop();
    },
    scrollTableToTop() {
      this.$nextTick(() => {
        const container = this.$refs.fileTableScroll;
        if (container && typeof container.scrollTop === "number") {
          container.scrollTop = 0;
        }
      });
    },
    confirmDeleteFolder(folder) {
      if (this.isEntityFolder(folder) || this.isGroupFolder(folder) || !folder?.backend_id) {
        this.toast.info("This folder cannot be deleted here.");
        return;
      }
      if (confirm(`Are you sure you want to delete folder "${folder.name}"? This will delete all its contents.`)) {
        this.deleteFolder(folder);
      }
    },
    async deleteFolder(folder) {
      const folderId = extractNumericId(folder?.backend_id ?? folder?.id);
      const deletedFolderPath = folder?.internal_path || null;

      if (!folderId) {
        this.toast.error("Failed to delete folder.");
        return;
      }

      try {
        this.deletingFolderId = folderId;
       await apiService.deleteResource(
          API_ENDPOINTS.folder(folderId),
          this.loggedInUser.id,
          this.loggedInUser.entra_id
        );
        this.toast.success("Folder deleted successfully!");
        this.foldersFetched = this.foldersFetched.filter((f) => {
          if (f.id === folderId) return false;
          if (!deletedFolderPath || !f.internal_path) return true;
          return !(f.internal_path === deletedFolderPath || f.internal_path.startsWith(`${deletedFolderPath}/`));
        });

        if (deletedFolderPath) {
          const fileIdsToRemove = this.files
            .filter(file => file.path && (file.path === deletedFolderPath || file.path.startsWith(`${deletedFolderPath}/`)))
            .map(file => file.id);

          this.locallyHiddenFileIds = Array.from(
            new Set([...this.locallyHiddenFileIds, ...fileIdsToRemove])
          );

          this.selectedFileIds = this.selectedFileIds.filter(
            (id) => !fileIdsToRemove.includes(id)
          );
        }

        if (this.currentFolder && this.currentFolder.backend_id === folderId) {
          this.currentFolder = null;
        }
        await this.fetchFolders();
      } catch (error) {
        console.error("Error deleting folder:", error);
        this.toast.error("Failed to delete folder.");
      } finally {
        this.deletingFolderId = null;
      }
    },
    // Methods for inline folder renaming in file table
    startEditingFolder(item) {
      if (this.isEntityFolder(item) || this.isGroupFolder(item) || !item?.backend_id) {
        return;
      }
      // Activate editing for this folder
      this.editingFolderId = item.id;
      this.editingFolderName = item.name;
    },
    async confirmRenameFolder(item) {
      if (this.isEntityFolder(item) || !item?.backend_id) {
        this.editingFolderId = null;
        this.editingFolderName = "";
        return;
      }
      // Only update if the new name is non-empty and different
      if (this.editingFolderName && this.editingFolderName !== item.name) {
        try {
          const payload = {
            name: this.editingFolderName, 
            };
          await apiService.updateResource(API_ENDPOINTS.folder(item.backend_id), payload, this.loggedInUser.id, this.loggedInUser.entra_id)
          this.toast.success("Folder renamed successfully");
          await this.fetchFolders();
        } catch (error) {
          console.error("Error renaming folder:", error);
          this.toast.error("Failed to rename folder");
        }
      }
      // Exit editing mode
      this.editingFolderId = null;
      this.editingFolderName = "";
    },
    setPage(pageNumber) {
      this.currentPage = pageNumber;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/main.scss";

.file-row i.bi-trash {
  cursor: pointer;
}

.folder-name {
  cursor: pointer;
}

.experiment-files {
  max-width: 800px;
  margin: 14px auto;
  padding: 14px;
  background: $secondary-color;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.files-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.title-wrap {
  display: flex;
  align-items: baseline;
  justify-content: flex-start;
  gap: 8px;
  padding-right: 12px;

  h3 {
    margin: 0;
    color: $primary-color;
  }
}

.meta-count {
  font-size: 0.92rem;
  font-weight: 500;
  color: rgba($primary-color, 0.8);
}

.header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding-right: 8px;
}

.bulk-actions {
  display: inline-flex;
  gap: 10px;
}

.bulk-btn {
  min-width: 34px;
  height: 34px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* Files container */
.files-container {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.folder-section {
  width: 250px;
  min-width: 250px;
  background-color: rgba($primary-color, 0.04);
  border: 1px solid $primary-color;
  border-radius: 8px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  transition: width 0.18s ease, min-width 0.18s ease, padding 0.18s ease;
}

.folder-section.is-collapsed {
  width: 56px;
  min-width: 56px;
  padding: 8px 6px;
}

.folder-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  width: 100%;
  gap: 6px;
}

.folder-section-title {
  font-size: 0.84rem;
  font-weight: 700;
  color: rgba($primary-color, 0.84);
}

.folder-header-spacer {
  flex: 1;
}

.folder-header-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.home-folder-wrapper {
  min-width: 0;
  margin-top: 8px;
}

.add-folder-wrapper {
  position: relative;
}

.folder-collapse-btn {
  width: 30px;
  height: 30px;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.folder-input-dropdown {
  position: absolute;
  top: 110%;
  right: 0;
  width: 150px;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 8px;
  z-index: 100;
  display: flex;
  flex-direction: column;
  gap: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.folder-input-dropdown input {
  width: 100%;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.folder-input-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 5px;
}

.folder-input-actions button {
  background: none !important;
  border: none !important;
  cursor: pointer;
  font-size: 16px !important;
  padding: 4px !important;
  color: #333 !important;
}

.folder-list {
  width: 100%;
  overflow-y: auto;
  max-height: 400px;
  margin-top: 8px;
  padding-right: 2px;
}

.folder-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.88rem;
  padding: 5px 8px;
  cursor: pointer;
  border-radius: 6px;
  min-height: 30px;
}

.folder-item.active {
  background-color: rgba($primary-color, 0.14);
}

.folder-item:hover {
  background-color: #fff;
}

.navigate-entity-btn {
  border: none;
  background: transparent;
  padding: 0 2px;
  cursor: pointer;
  color: $primary-color;
  display: inline-flex;
  align-items: center;
  font-size: 1.1rem;
  line-height: 1;
  opacity: 0.7;
  border-radius: 4px;
  transition: opacity 0.15s, background 0.15s;

  &:hover {
    opacity: 1;
    background: rgba($primary-color, 0.1);
  }
}

.files-table-section {
  flex: 1;
  min-width: 0;
  border: 1px solid rgba($primary-color, 0.45);
  border-radius: 8px;
  padding: 8px;
  background: white;
  display: flex;
  flex-direction: column;
  position: relative;
}

.table-scroll {
  overflow: auto;
}

.files-table-section.loading .table-scroll {
  opacity: 0.45;
  pointer-events: none;
}

.table-loading-overlay {
  position: absolute;
  top: 48px;
  left: 8px;
  right: 8px;
  bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 6px;
  z-index: 3;
}

.loading-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: $primary-color;
  font-size: 0.9rem;
  font-weight: 500;
}

.tree-loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba($primary-color, 0.25);
  border-top-color: $primary-color;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.current-path-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.path-btn {
  height: 30px;
  min-width: 30px;
  padding: 0;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.current-path {
  min-width: 0;
  flex: 1;
  font-size: 0.84rem;
  color: rgba($primary-color, 0.88);
  padding: 5px 8px;
  border-radius: 6px;
  background: rgba($primary-color, 0.08);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.file-table th,
.file-table td {
  padding: 6px 8px;
  text-align: left;
  border-bottom: 1px solid rgba($primary-color, 0.15);
  vertical-align: middle;
}

.file-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
  font-size: 0.82rem;
  color: rgba($primary-color, 0.85);
}

.type-header {
  width: 34px;
}

.file-row:hover {
  background-color: rgba($primary-color, 0.05);
}

.file-row--selectable {
  cursor: pointer;
}

.file-row--selected {
  background-color: rgba($primary-color, 0.12);
}

.file-row--selected:hover {
  background-color: rgba($primary-color, 0.16);
}

.file-name-cell {
  max-width: 200px;
  overflow: hidden;
  padding: 8px;
  white-space: nowrap;
}

.file-name {
  display: block; 
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: $primary-color;
  font-size: 0.9rem;
}

.entity-folder-tag {
  margin-left: 6px;
  font-size: 11px;
  text-transform: uppercase;
  color: $primary-color;
  background-color: rgba($primary-color, 0.12);
  border-radius: 10px;
  padding: 1px 6px;
}

.clickable {
  cursor: pointer;
}

.rename-input {
  width: 100%;
  box-sizing: border-box;
  padding: 2px 4px;
}

.comment-container {
  position: relative;
  display: inline-flex;
}

.actions-header {
  width: 136px;
  text-align: right;
}

.actions-cell {
  width: 136px;
  text-align: right;
}

.row-actions {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  width: 100%;
}

.row-actions__secondary {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  transform: translateX(4px);
  pointer-events: none;
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.file-row:hover .row-actions__secondary,
.file-row:focus-within .row-actions__secondary,
.row-actions--visible .row-actions__secondary {
  opacity: 1;
  transform: translateX(0);
  pointer-events: auto;
}

.row-action-btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba($primary-color, 0.16);
  background: #fff;
  color: rgba($primary-color, 0.75);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: rgba($primary-color, 0.3);
    background: rgba($primary-color, 0.08);
    color: $primary-color;
  }
}

.row-action-btn--active,
.row-action-btn--has-value {
  color: $primary-color;
}

.row-action-btn--has-value {
  background: rgba($primary-color, 0.08);
}

.row-action-btn--static {
  cursor: default;
  pointer-events: none;
}

.comment-action-btn {
  position: relative;
}

.display-image-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.display-image-toggle__btn {
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid rgba($primary-color, 0.16);
  background: #fff;
  color: rgba($primary-color, 0.75);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.display-image-toggle__btn--active {
  background: rgba($third-color, 0.7);
  color: $primary-color;
  border-color: rgba($primary-color, 0.32);
}

.display-order-badge {
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 999px;
  background: rgba($primary-color, 0.1);
  color: $primary-color;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.76rem;
  font-weight: 700;
}

.comment-tooltip {
  position: fixed;
  background: white;
  padding: 10px;
  border: 1px solid $primary-color;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  white-space: normal;
  word-wrap: break-word;
  max-width: 250px;
  z-index: 10000;
  pointer-events: none;
}

.comment-editor {
  width: min(420px, calc(100vw - 32px));
  max-width: min(420px, calc(100vw - 32px));
  box-sizing: border-box;
  background: white;
  padding: 8px;
  border: 1px solid $primary-color;
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.22);
}

.comment-editor-backdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 12000;
}

.comment-editor--popover {
  position: fixed;
  z-index: 12001;
}

.comment-editor__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}

.comment-editor__close {
  border: none;
  background: transparent;
  color: rgba($primary-color, 0.8);
  cursor: pointer;
  font-size: 1.4rem;
  line-height: 1;
  padding: 0 4px;
}

.comment-editor textarea {
  width: 100%;
  min-height: 120px;
  resize: vertical;
  box-sizing: border-box;
  border: 1px solid $form-color;
  padding: 6px;
}

.comment-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 4px;
}

.comment-actions button {
  background: none !important;
  border: none !important;
  cursor: pointer;
  font-size: 16px !important;
  padding: 4px !important;
  color: #333 !important;
}

.upload-controls {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  border-top: 1px solid rgba($primary-color, 0.2);
  padding-top: 10px;
}

.upload-info {
  display: flex;
  align-items: center;
  gap: 10px;
}

.selected-count {
  font-size: 0.84rem;
  color: rgba($primary-color, 0.85);
}

.upload-btn {
  padding: 7px 12px;
  background: $primary-color;
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}

.upload-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cancel-icon {
  font-size: 20px;
  cursor: pointer;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.save-btn {
  background-color: #4caf50;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

.cancel-btn {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
}

/* Pagination styling */
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 10px;
  flex-wrap: wrap;
  gap: 4px;
}
.pagination button {
  margin: 0;
  padding: 4px 8px;
  background-color: $primary-color;
  border: 1px solid $secondary-color;
  border-radius: 5px;
  cursor: pointer;
  font-size: 0.82rem;
}
.pagination button:hover,
.pagination button.active {
  background-color: darken($primary-color, 10%);
}
.folder-delete-spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.hidden {
  display: none;
}

.upload-info {
  display: flex;
  align-items: center;
  gap: 15px;
}

.upload-icon {
  font-size: 20px;
  cursor: pointer;
  transition: transform 0.2s ease, color 0.2s ease;
}

.upload-icon:hover {
  transform: scale(1.2);
  color: darken($primary-color, 10%);
}

.files-header {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
}

.upload-progress-inline {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: unset;
  justify-content: unset;
}

.upload-progress-inline progress {
  width: 140px;
  height: 10px;
}

.upload-progress-inline .percent {
  font-weight: 600;
}

.upload-progress-inline .label {
  font-size: 12px;
  opacity: 0.8;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.empty-state {
  text-align: center !important;
  color: rgba($primary-color, 0.75);
  font-size: 0.9rem;
  padding: 18px 8px !important;
}

</style>
